import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Tooltip, Empty, Table, Tag } from 'antd';
import { useSelector } from 'react-redux';
import { beeXLogger } from '../../../../helpers/logger';
import { EventRanking } from '../EventRanking';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useMonitoringDataWithInterval } from '../../../../hooks/useMonitoringDataWithInterval';
import { useMonitoringDataFetch } from '../../../../hooks/useMonitoringDataFetch';

import '../ImageQuality/index.scss';
import { StateTag } from '../../../../components/StateTag';
import { DATA_LOSS_TYPE } from '../utils';

const DISCONNECTION_DURATION_QUESTION_ID = 291;

const SEVERITY_CONFIG_MAP = {
  'Data loss': {
    color: 'red',
    textClass: 'confirmed',
  },
  'Confirmed data loss': {
    color: 'red',
    textClass: 'confirmed',
  },
  'Suspected data loss': {
    color: 'gold',
    textClass: 'suspected',
  },
  'Intra-day interruption': {
    color: 'orange',
    textClass: 'lost-connection',
  },
  Normal: {
    color: 'green',
    textClass: 'online',
  },
};

const formatDuration = (seconds) => {
  if (typeof seconds !== 'number' || isNaN(seconds)) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);
  return parts.join(' ');
};

const stripEmoji = (str = '') =>
  str
    .replace(/[\u{1F300}-\u{1FFFF}]|[\u2600-\u26FF]|[\u2700-\u27BF]/gu, '')
    .trim();

const getSeverityConfig = (severityRaw, dataLossType) => {
  let label = stripEmoji(severityRaw);
  // if (label === 'Data loss') {
  //   if (dataLossType === DATA_LOSS_TYPE.CAM_ON_GW_ON) {
  //     label = 'Intra-day interruption';
  //   } else if (dataLossType === DATA_LOSS_TYPE.CAM_OFF_GW_ON) {
  //     label = 'Data loss';
  //   }
  // }
  const config = SEVERITY_CONFIG_MAP[label] ?? SEVERITY_CONFIG_MAP['Normal'];
  return { ...config, label };
};

const transformData = (rawData) =>
  rawData.map((item, index) => ({
    id: item.camera_serial,
    index: String(index + 1).padStart(2, '0'),
    cameraName: item.camera_name,
    cameraSerial: item.camera_serial,
    gatewaySerial: item.gateway_serial,
    cameraState: parseStateFromString(item.camera_status),
    gatewayState: parseStateFromString(item.gateway_status),
    dataLossType: item.data_loss_type,
    severity: item.severity,
    downtimeSeconds: item.downtime_seconds,
    durationString: formatDuration(item.downtime_seconds),
    offlineSince: item.offline_since,
    severityConfig: getSeverityConfig(item.severity, item.data_loss_type),
  }));

const parseStateFromString = (statusStr = '') => {
  const lower = statusStr.toLowerCase();
  if (lower.includes('online')) return 1;
  if (lower.includes('offline')) return 0;
  return -1;
};

const columns = [
  {
    title: 'Camera name',
    dataIndex: 'cameraName',
    key: 'cameraName',
    ellipsis: { showTitle: false },
    render: (name, record) => (
      <Tooltip title={`${name} (${record.cameraSerial})`} placement="topLeft">
        <span className="camera-name">{name}</span>
      </Tooltip>
    ),
  },
  {
    title: 'Camera status',
    dataIndex: 'cameraState',
    key: 'cameraState',
    render: (state) => <StateTag state={state} />,
  },
  {
    title: 'Gateway status',
    dataIndex: 'gatewayState',
    key: 'gatewayState',
    render: (state) => <StateTag state={state} />,
  },
  {
    title: 'Duration',
    dataIndex: 'durationString',
    key: 'durationString',
    sorter: (a, b) => a.downtimeSeconds - b.downtimeSeconds,
    render: (durationString, record) => (
      <span className={record.severityConfig.textClass}>{durationString}</span>
    ),
  },
  {
    title: 'Severity',
    key: 'severityLabel',
    dataIndex: 'severityConfig',
    filters: [
      { text: 'Data loss', value: 'Data loss' },
      { text: 'Suspected data loss', value: 'Suspected data loss' },
      { text: 'Intra-day interruption', value: 'Intra-day interruption' },
      { text: 'Normal', value: 'Normal' },
    ],
    onFilter: (value, record) => record.severityConfig.label === value,
    render: (cfg) => <Tag color={cfg.color}>{cfg.label}</Tag>,
  },
  // {
  //   title: 'Type',
  //   dataIndex: 'dataLossType',
  //   key: 'dataLossType',
  //   ellipsis: { showTitle: false },
  //   render: (type) => (
  //     <Tooltip title={type} placement="topLeft">
  //       <span>{type}</span>
  //     </Tooltip>
  //   ),
  // },
  {
    title: 'Offline Since',
    dataIndex: 'offlineSince',
    key: 'offlineSince',
    render: (val) => (val ? <span>{val}</span> : '_'),
  },
];

const DisconnectionDuration = ({
  isActive = true,
  intervalMs = 0,
  onRegisterReload,
  selectedDate,
}) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const { namespace } = useSelector((state) => state);
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  const baseFetchData = useMonitoringDataFetch(
    DISCONNECTION_DURATION_QUESTION_ID,
    selectedDate,
  );

  const fetchDisconnectionDurationData = useCallback(async () => {
    if (!namespace) return null;

    try {
      setIsLoading(true);
      const rawData = await baseFetchData();
      console.log('🚀 ~ DisconnectionDuration ~ rawData:', rawData);
      const transformedData = transformData([...rawData]);

      setData(transformedData);
      if (isMountedRef.current) {
        setIsLoading(false);
        if (loadingTimerRef.current) {
          clearTimeout(loadingTimerRef.current);
          loadingTimerRef.current = null;
        }
      }
      return transformedData;
    } catch (err) {
      beeXLogger.error(err);
      throw err;
    }
  }, [baseFetchData, namespace]);

  const { reload } = useMonitoringDataWithInterval(
    fetchDisconnectionDurationData,
    isActive ? intervalMs : 0,
    [namespace, isActive],
  );

  useEffect(() => {
    if (onRegisterReload && typeof onRegisterReload === 'function') {
      onRegisterReload(reload);
    }
  }, [reload, onRegisterReload]);

  useEffect(() => {
    if (isActive && isLoading && !loadingTimeout) {
      loadingTimerRef.current = setTimeout(() => {
        if (isMountedRef.current && isLoading) setLoadingTimeout(true);
      }, 30000);
    }
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, [isActive, isLoading, loadingTimeout]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    };
  }, []);

  return (
    <EventRanking
      headerIcon={<ClockCircleOutlined />}
      headerTitle="Top Total Duration"
      headerSubTitle="Cumulative (24h)"
      showAll={false}
      hasMore={false}
    >
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={isLoading}
        size="small"
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          size: 'small',
        }}
        locale={{
          emptyText:
            loadingTimeout || !isLoading ? (
              <Empty description="No data" />
            ) : null,
        }}
        scroll={{ x: 'max-content' }}
        className="top-disconnection-duration-table"
      />
    </EventRanking>
  );
};

export default DisconnectionDuration;
